import type { TemperaLayerImage } from '../types';

// packages/lyric-stage/src/services/temperaLayerImages.ts
// In-memory image pool for standalone hosts (no Folia IndexedDB).

export interface StoredTemperaLayerImage {
  id: string;
  name: string;
  mimeType: string;
  blob: Blob;
  thumbnail?: Blob;
}

const store = new Map<string, StoredTemperaLayerImage>();

let idSeq = 0;

export const isSupportedTemperaLayerImageFile = (file: File) =>
  /^image\//.test(file.type) || /\.(png|jpe?g|gif|webp|svg)$/i.test(file.name);

export const getTemperaLayerImage = async (id: string) => store.get(id) ?? null;

export const saveTemperaLayerImage = async (image: StoredTemperaLayerImage) => {
  store.set(image.id, image);
};

export const clearTemperaLayerImage = async (id: string) => {
  store.delete(id);
};

export const buildStoredTemperaLayerImage = (file: File): StoredTemperaLayerImage => ({
  id: `tempera_${Date.now()}_${idSeq++}`,
  name: file.name,
  mimeType: file.type || 'application/octet-stream',
  blob: file,
});

export const createTemperaLayerImageThumbnail = async (_file: Blob): Promise<Blob | null> => null;

export const prepareTemperaLayerImage = async (file: File): Promise<StoredTemperaLayerImage> => {
  const stored = buildStoredTemperaLayerImage(file);
  await saveTemperaLayerImage(stored);
  return stored;
};

export const loadTemperaLayerImageThumbnails = async (
  placements: Pick<TemperaLayerImage, 'id'>[],
): Promise<Map<string, Blob>> => {
  const blobs = new Map<string, Blob>();
  await Promise.all(placements.map(async (placement) => {
    const stored = await getTemperaLayerImage(placement.id).catch(() => null);
    const preview = stored?.thumbnail ?? stored?.blob;
    if (preview) blobs.set(placement.id, preview);
  }));
  return blobs;
};

export const loadTemperaLayerImageBlobs = async (
  placements: Pick<TemperaLayerImage, 'id'>[],
): Promise<Map<string, Blob>> => {
  const blobs = new Map<string, Blob>();
  await Promise.all(placements.map(async (placement) => {
    const stored = await getTemperaLayerImage(placement.id).catch(() => null);
    if (stored?.blob) blobs.set(placement.id, stored.blob);
  }));
  return blobs;
};
